import React, { useState, useEffect } from 'react';
import { Plus, Trash2, AlertCircle, Calendar, Syringe, Shield, CheckCircle, X, Thermometer, Stethoscope } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';
import { motion } from 'framer-motion';

interface Immunization {
  id: string;
  name: string;
  date_given: string;
  provider_id?: string;
  lot_number?: string;
  next_due_date?: string;
  notes?: string;
}

export function Immunizations() {
  const { user } = useAuth();
  const [immunizations, setImmunizations] = useState<Immunization[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImmunization, setSelectedImmunization] = useState<Immunization | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    date_given: '',
    provider_id: '',
    lot_number: '',
    next_due_date: '',
    notes: ''
  });

  // Common vaccines
  const commonVaccines = [
    'COVID-19',
    'Influenza (Flu)',
    'Tetanus/Diphtheria/Pertussis (Tdap)',
    'Measles/Mumps/Rubella (MMR)',
    'Hepatitis A',
    'Hepatitis B',
    'Human Papillomavirus (HPV)',
    'Pneumococcal',
    'Shingles (Zoster)',
    'Varicella (Chickenpox)'
  ];

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  useEffect(() => {
    const handler = () => { if (user) loadData(true); };
    window.addEventListener('refresh-health', handler);
    return () => window.removeEventListener('refresh-health', handler);
  }, [user]);

  async function loadData(background = false) {
    try {
      if (!background) setLoading(true);
      const { data, error } = await supabase
        .from('immunizations')
        .select('*')
        .eq('user_id', user?.id)
        .order('date_given', { ascending: false });

      if (error) throw error;
      setImmunizations(data || []);
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : 'Failed to load immunizations');
    } finally {
      if (!background) setLoading(false);
    }
  }

  const handleSaveImmunization = async () => {
    try {
      const { data, error } = await supabase
        .from('immunizations')
        .insert([{
          ...formData,
          user_id: user?.id
        }])
        .select()
        .single();

      if (error) throw error;

      setImmunizations(prev => [data, ...prev]);
      setShowAddModal(false);
      setFormData({
        name: '',
        date_given: '',
        provider_id: '',
        lot_number: '',
        next_due_date: '',
        notes: ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save immunization');
    }
  };

  const handleDeleteImmunization = async (id: string) => {
    try {
      const { error } = await supabase
        .from('immunizations')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setImmunizations(prev => prev.filter(immunization => immunization.id !== id));
      if (selectedImmunization?.id === id) {
        setSelectedImmunization(null);
        setShowDetailModal(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete immunization');
    }
  };

  const handleViewImmunization = (immunization: Immunization) => {
    setSelectedImmunization(immunization);
    setShowDetailModal(true);
  };

  const filteredImmunizations = immunizations.filter(immunization =>
    immunization.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    immunization.provider_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group immunizations by year
  const groupedImmunizations = filteredImmunizations.reduce((acc, immunization) => {
    const year = new Date(immunization.date_given).getFullYear();
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(immunization);
    return acc;
  }, {} as Record<number, Immunization[]>);

  // Sort years in descending order
  const sortedYears = Object.keys(groupedImmunizations)
    .map(year => parseInt(year))
    .sort((a, b) => b - a);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Immunizations</h1>
          <p className="text-gray-600 mt-1">Track your vaccination history</p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowAddModal(true)}
          icon={<Plus size={20} />}
        >
          Add Immunization
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="text-red-500" size={20} />
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <SearchInput
        placeholder="Search immunizations..."
        value={searchQuery}
        onChange={setSearchQuery}
      />

      {filteredImmunizations.length > 0 ? (
        <div className="space-y-6">
          {sortedYears.map(year => (
            <div key={year} className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">{year}</h2>
              <div className="space-y-3">
                {groupedImmunizations[year].map((immunization) => (
                  <motion.div
                    key={immunization.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Syringe className="text-green-600" size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 mb-1">
                            {immunization.name}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {new Date(immunization.date_given).toLocaleDateString()}
                            </span>
                            {immunization.provider_id && (
                              <span>{immunization.provider_id}</span>
                            )}
                          </div>

                          <div className="mt-3 pl-11 space-y-1">
                            {immunization.lot_number && (
                              <p className="text-sm text-gray-600">
                                Lot Number: {immunization.lot_number}
                              </p>
                            )}
                            
                            {immunization.provider_id && (
                              <p className="text-sm text-gray-600">
                                Provider: {immunization.provider_id}
                              </p>
                            )}
                            
                            {immunization.next_due_date && (
                              <div className="flex items-center gap-1 text-sm text-green-700 mt-2">
                                <Calendar size={14} />
                                <span>Next dose: {new Date(immunization.next_due_date).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewImmunization(immunization)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <CheckCircle size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteImmunization(immunization.id)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Syringe className="text-green-400" size={24} />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Immunizations Found</h3>
          <p className="text-gray-500 mb-4">
            {searchQuery
              ? 'No immunizations match your search criteria'
              : 'You haven\'t added any immunizations yet'}
          </p>
          {!searchQuery && (
            <Button
              variant="outline"
              onClick={() => setShowAddModal(true)}
            >
              Add Your First Immunization
            </Button>
          )}
        </div>
      )}

      {/* Immunization Status */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Immunization Status</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Shield className="text-green-500" size={20} />
                <span className="text-gray-900">COVID-19</span>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                immunizations.some(i => i.name.toLowerCase().includes('covid'))
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {immunizations.some(i => i.name.toLowerCase().includes('covid'))
                  ? 'Up to date'
                  : 'Not recorded'}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Shield className="text-green-500" size={20} />
                <span className="text-gray-900">Influenza (Flu)</span>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                immunizations.some(i => i.name.toLowerCase().includes('flu') || i.name.toLowerCase().includes('influenza'))
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {immunizations.some(i => i.name.toLowerCase().includes('flu') || i.name.toLowerCase().includes('influenza'))
                  ? 'Up to date'
                  : 'Not recorded'}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Add Immunization Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Immunization"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vaccine Name
            </label>
            <select
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a vaccine</option>
              {commonVaccines.map(vaccine => (
                <option key={vaccine} value={vaccine}>{vaccine}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Administered
            </label>
            <input
              type="date"
              value={formData.date_given}
              onChange={(e) => setFormData(prev => ({ ...prev, date_given: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Provider
            </label>
            <input
              type="text"
              value={formData.provider_id}
              onChange={(e) => setFormData(prev => ({ ...prev, provider_id: e.target.value }))}
              placeholder="Healthcare provider name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lot Number
            </label>
            <input
              type="text"
              value={formData.lot_number}
              onChange={(e) => setFormData(prev => ({ ...prev, lot_number: e.target.value }))}
              placeholder="Vaccine lot number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Next Dose Due
            </label>
            <input
              type="date"
              value={formData.next_due_date}
              onChange={(e) => setFormData(prev => ({ ...prev, next_due_date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="primary"
              onClick={handleSaveImmunization}
              className="flex-1"
            >
              Save Immunization
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Immunization Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Immunization Details"
      >
        {selectedImmunization && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vaccine Name
              </label>
              <p className="text-gray-900">{selectedImmunization.name}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Administered
              </label>
              <p className="text-gray-900">
                {new Date(selectedImmunization.date_given).toLocaleDateString()}
              </p>
            </div>

            {selectedImmunization.provider_id && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provider
                </label>
                <p className="text-gray-900">{selectedImmunization.provider_id}</p>
              </div>
            )}

            {selectedImmunization.lot_number && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lot Number
                </label>
                <p className="text-gray-900">{selectedImmunization.lot_number}</p>
              </div>
            )}

            {selectedImmunization.next_due_date && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Next Dose Due
                </label>
                <p className="text-gray-900">
                  {new Date(selectedImmunization.next_due_date).toLocaleDateString()}
                </p>
              </div>
            )}

            {selectedImmunization.notes && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <p className="text-gray-900">{selectedImmunization.notes}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="destructive"
                onClick={() => {
                  handleDeleteImmunization(selectedImmunization.id);
                  setShowDetailModal(false);
                }}
                className="flex-1"
              >
                Delete
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDetailModal(false)}
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// Helper components for this file only
function Building({ size, className }: { size: number, className?: string }) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
      <path d="M9 22v-4h6v4"></path>
      <path d="M8 6h.01"></path>
      <path d="M16 6h.01"></path>
      <path d="M12 6h.01"></path>
      <path d="M12 10h.01"></path>
      <path d="M12 14h.01"></path>
      <path d="M16 10h.01"></path>
      <path d="M16 14h.01"></path>
      <path d="M8 10h.01"></path>
      <path d="M8 14h.01"></path>
    </svg>
  );
}

function Barcode({ size, className }: { size: number, className?: string }) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      <path d="M3 5v14"></path>
      <path d="M8 5v14"></path>
      <path d="M12 5v14"></path>
      <path d="M17 5v14"></path>
      <path d="M21 5v14"></path>
    </svg>
  );
}